/** Conjunctions used when a sharing-rule selector string joins predicates. */
export const JSONATA_AND_CONJUNCTION = ' and ';
export const JSONATA_OR_CONJUNCTION = ' or ';

/**
 * Split a compound JSONata predicate string into separate predicates for display (one line each).
 * Does not split {@link JSONATA_AND_CONJUNCTION} inside single-quoted literals (incl. escaped \\').
 * @param {unknown} text
 * @returns {string[]}
 */
export function splitJsonataPredicatesJoinedByAnd(text) {
    const s = String(text ?? '');
    const trimmed = s.trim();
    if (!trimmed.includes(JSONATA_AND_CONJUNCTION)) {
        return [s];
    }
    /** Merged JSONata predicates never start with `{`/`[`; JSON selector lines may contain `"… and …"`. */
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return [s];
    }

    const parts = [];
    let i = 0;
    let buf = '';
    let inString = false;

    while (i < s.length) {
        const ch = s[i];

        if (inString) {
            buf += ch;
            if (ch === '\\' && i + 1 < s.length) {
                buf += s[i + 1];
                i += 2;
                continue;
            }
            if (ch === "'") {
                inString = false;
            }
            i += 1;
            continue;
        }

        if (ch === "'") {
            inString = true;
            buf += ch;
            i += 1;
            continue;
        }

        if (s.startsWith(JSONATA_AND_CONJUNCTION, i)) {
            const part = buf.trim();
            if (part) {
                parts.push(part);
            }
            buf = '';
            i += JSONATA_AND_CONJUNCTION.length;
            continue;
        }

        buf += ch;
        i += 1;
    }

    const lastPart = buf.trim();
    if (lastPart) {
        parts.push(lastPart);
    }

    return parts.length > 0 ? parts : [s];
}

function matchConjunction(text, index) {
    const rest = text.slice(index);
    if (rest.length >= 5 && rest.slice(0, 5).toLowerCase() === JSONATA_AND_CONJUNCTION) {
        return { op: 'and', len: JSONATA_AND_CONJUNCTION.length };
    }
    if (rest.length >= 4 && rest.slice(0, 4).toLowerCase() === JSONATA_OR_CONJUNCTION) {
        return { op: 'or', len: JSONATA_OR_CONJUNCTION.length };
    }
    return null;
}

function isJsonSelectorLiteral(text) {
    const trimmed = String(text ?? '').trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
}

/**
 * True when the whole string is wrapped in grouping parentheses, not a function call.
 * @param {string} text
 * @returns {boolean}
 */
function isWrappedInGroupingParens(text) {
    const s = String(text ?? '').trim();
    if (!s.startsWith('(') || !s.endsWith(')')) return false;
    let depth = 0;
    let inString = false;
    for (let i = 0; i < s.length; i += 1) {
        const ch = s[i];
        if (inString) {
            if (ch === '\\' && i + 1 < s.length) {
                i += 1;
                continue;
            }
            if (ch === "'") inString = false;
            continue;
        }
        if (ch === "'") {
            inString = true;
            continue;
        }
        if (ch === '(') depth += 1;
        else if (ch === ')') {
            depth -= 1;
            if (depth === 0) return i === s.length - 1;
        }
    }
    return false;
}

/**
 * Split on top-level ` and ` / ` or ` (depth 0, outside single quotes).
 * @param {string} text
 * @returns {string[]}
 */
function splitTopLevel(text) {
    const s = String(text ?? '');
    const parts = [];
    let buf = '';
    let depth = 0;
    let inString = false;

    for (let i = 0; i < s.length; i += 1) {
        const ch = s[i];
        if (inString) {
            buf += ch;
            if (ch === '\\' && i + 1 < s.length) {
                buf += s[i + 1];
                i += 1;
                continue;
            }
            if (ch === "'") inString = false;
            continue;
        }
        if (ch === "'") {
            inString = true;
            buf += ch;
            continue;
        }
        if (ch === '(') {
            depth += 1;
            buf += ch;
            continue;
        }
        if (ch === ')') {
            depth = Math.max(0, depth - 1);
            buf += ch;
            continue;
        }
        if (depth === 0) {
            const match = matchConjunction(s, i);
            if (match) {
                const atom = buf.trim();
                if (atom) parts.push(atom);
                parts.push(match.op);
                buf = '';
                i += match.len - 1;
                continue;
            }
        }
        buf += ch;
    }

    const last = buf.trim();
    if (last) parts.push(last);
    return parts;
}

/**
 * @param {string} text
 * @returns {{ type: 'atom', text: string } | { type: 'and' | 'or', left: object, right: object } | null}
 */
function parseExpression(text) {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return null;
    if (isJsonSelectorLiteral(trimmed)) return { type: 'atom', text: trimmed };
    if (isWrappedInGroupingParens(trimmed)) {
        return parseExpression(trimmed.slice(1, -1).trim());
    }
    const parts = splitTopLevel(trimmed);
    if (parts.length <= 1) return { type: 'atom', text: trimmed };
    return parseTokenList(parts);
}

/**
 * `and` binds tighter than `or`, matching JSONata. Parentheses are already split out.
 * @param {string[]} parts
 */
function parseTokenList(parts) {
    const nodes = [];
    const ops = [];
    for (let i = 0; i < parts.length; i += 1) {
        if (i % 2 === 0) {
            const node = parseExpression(parts[i]);
            if (node) nodes.push(node);
        } else {
            ops.push(parts[i] === 'or' ? 'or' : 'and');
        }
    }
    if (nodes.length === 0) return null;

    let current = nodes[0];
    const orNodes = [];
    for (let i = 0; i < ops.length && i + 1 < nodes.length; i += 1) {
        if (ops[i] === 'and') {
            current = { type: 'and', left: current, right: nodes[i + 1] };
        } else {
            orNodes.push(current);
            current = nodes[i + 1];
        }
    }
    orNodes.push(current);

    let result = orNodes[0];
    for (let i = 1; i < orNodes.length; i += 1) {
        result = { type: 'or', left: result, right: orNodes[i] };
    }
    return result;
}

function isPureOp(node, op) {
    if (!node || node.type === 'atom') return true;
    if (node.type !== op) return false;
    return isPureOp(node.left, op) && isPureOp(node.right, op);
}

function collectAtoms(node) {
    if (!node) return [];
    if (node.type === 'atom') return [node.text.trim()];
    return [...collectAtoms(node.left), ...collectAtoms(node.right)];
}

function serializeTree(node) {
    if (!node) return '';
    if (node.type === 'atom') return node.text.trim();
    if (isPureOp(node, 'and')) return collectAtoms(node).join(JSONATA_AND_CONJUNCTION);
    if (isPureOp(node, 'or')) return collectAtoms(node).join(JSONATA_OR_CONJUNCTION);
    const leftText =
        node.left?.type === 'atom' ? serializeTree(node.left) : `(${serializeTree(node.left)})`;
    const rightText =
        node.right?.type === 'atom' ? serializeTree(node.right) : `(${serializeTree(node.right)})`;
    const op = node.type === 'or' ? JSONATA_OR_CONJUNCTION : JSONATA_AND_CONJUNCTION;
    return `${leftText}${op}${rightText}`;
}

function parenthesizeExpression(text) {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return trimmed;
    if (isWrappedInGroupingParens(trimmed)) return trimmed;
    if (splitTopLevel(trimmed).length > 1) return `(${trimmed})`;
    return trimmed;
}

/**
 * @param {object|null} node
 * @param {'and'|'or'|null} operatorBefore
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
function flattenNode(node, operatorBefore) {
    if (!node) return [];
    if (node.type === 'atom') {
        const text = node.text.trim();
        return text ? [{ text, operatorBefore: operatorBefore ?? null }] : [];
    }
    const left = flattenNode(node.left, operatorBefore);
    if (!node.right || node.right.type === 'atom') {
        const text = node.right?.text?.trim() ?? '';
        if (!text) return left;
        return [...left, { text, operatorBefore: node.type === 'or' ? 'or' : 'and' }];
    }
    const rightText = parenthesizeExpression(serializeTree(node.right));
    if (!rightText) return left;
    return [...left, { text: rightText, operatorBefore: node.type === 'or' ? 'or' : 'and' }];
}

function chainKind(clauses) {
    const ops = clauses.slice(1).map((clause) => (clause.operatorBefore === 'or' ? 'or' : 'and'));
    if (ops.length === 0) return 'single';
    if (ops.every((op) => op === 'and')) return 'and';
    if (ops.every((op) => op === 'or')) return 'or';
    return 'mixed';
}

function serializeClausesToString(clauses) {
    const kind = chainKind(clauses);
    if (kind === 'single') return clauses[0].text.trim();
    if (kind === 'and') {
        return clauses.map((clause) => clause.text.trim()).join(JSONATA_AND_CONJUNCTION);
    }
    if (kind === 'or') {
        return clauses.map((clause) => clause.text.trim()).join(JSONATA_OR_CONJUNCTION);
    }
    let expr = clauses[0].text.trim();
    for (let i = 1; i < clauses.length; i += 1) {
        const op = clauses[i].operatorBefore === 'or' ? JSONATA_OR_CONJUNCTION : JSONATA_AND_CONJUNCTION;
        const right = clauses[i].text.trim();
        expr = i === 1 ? `${expr}${op}${right}` : `(${expr})${op}${right}`;
    }
    return expr;
}

function normalizeClause(clause, index) {
    return {
        text: String(clause?.text ?? '').trim(),
        operatorBefore: index === 0 ? null : clause?.operatorBefore === 'or' ? 'or' : 'and'
    };
}

/**
 * Parse stored `selectors` into a left-to-right chain.
 * Separate array entries are OR. `and` binds tighter than `or` unless parentheses say otherwise.
 * @param {unknown} items
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function parseSelectorChain(items) {
    const arr = Array.isArray(items) ? items : items == null ? [] : [items];
    const texts = [];
    for (const item of arr) {
        if (typeof item !== 'string') continue;
        const text = item.trim();
        if (text) texts.push(text);
    }
    if (texts.length === 0) return [];

    let tree = parseExpression(texts[0]);
    for (let i = 1; i < texts.length; i += 1) {
        const right = parseExpression(texts[i]);
        if (!tree) tree = right;
        else if (right) tree = { type: 'or', left: tree, right };
    }
    if (!tree) return [];
    const clauses = flattenNode(tree, null).filter((clause) => clause.text);
    if (clauses.length > 0) clauses[0] = { ...clauses[0], operatorBefore: null };
    return unwrapHiddenAndWrappedVisible(clauses);
}

/**
 * `$exists(...) and (A or B)` flattens to a hidden clause plus one parenthesized group.
 * Expand that group so the editor still shows A OR B, while save wraps it again.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]} clauses
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
function unwrapHiddenAndWrappedVisible(clauses) {
    let hiddenCount = 0;
    while (
        hiddenCount < clauses.length &&
        isHiddenLegacyShipmentIdentifierPredicate(clauses[hiddenCount].text)
    ) {
        hiddenCount += 1;
    }
    const rest = clauses.slice(hiddenCount);
    if (
        hiddenCount === 0 ||
        rest.length !== 1 ||
        rest[0].operatorBefore !== 'and' ||
        !isWrappedInGroupingParens(rest[0].text)
    ) {
        return clauses;
    }
    const inner = flattenNode(parseExpression(rest[0].text), 'and').filter((clause) => clause.text);
    if (inner.length <= 1) return clauses;
    const unwrapped = [...clauses.slice(0, hiddenCount), ...inner];
    unwrapped[0] = { ...unwrapped[0], operatorBefore: null };
    return unwrapped;
}

/**
 * Serialize a chain to one selector string. Pure AND/OR chains omit extra parentheses.
 * Mixed chains parenthesize left-to-right so JSONata precedence cannot regroup them.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @returns {string[]}
 */
function partitionHiddenExistsClauses(clauses) {
    const hidden = [];
    const visible = [];
    for (const clause of clauses) {
        if (isHiddenLegacyShipmentIdentifierPredicate(clause.text)) hidden.push(clause);
        else visible.push(clause);
    }
    return { hidden, visible };
}

/**
 * Legacy `$exists(ReferenceNumbersList...)` checks are hidden in the editor.
 * AND them once around the whole visible expression so an OR branch cannot skip the check.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]} clauses
 * @returns {string[]}
 */
function serializeWithHiddenExistsHoisted(clauses) {
    const { hidden, visible } = partitionHiddenExistsClauses(clauses);
    const visibleNormalized = visible.map((clause, index) => normalizeClause(clause, index));
    const visibleExpr = visibleNormalized.length ? serializeClausesToString(visibleNormalized) : '';
    if (hidden.length === 0) return visibleExpr ? [visibleExpr] : [];

    const hiddenExpr = hidden.map((clause) => clause.text.trim()).join(JSONATA_AND_CONJUNCTION);
    if (!visibleExpr) return [hiddenExpr];

    const visibleKind = chainKind(visibleNormalized);
    const wrappedVisible =
        visibleKind === 'or' || visibleKind === 'mixed' ? `(${visibleExpr})` : visibleExpr;
    return [`${hiddenExpr}${JSONATA_AND_CONJUNCTION}${wrappedVisible}`];
}

export function serializeSelectorChain(clauses) {
    const list = (clauses || []).map(normalizeClause).filter((clause) => clause.text);
    if (list.length === 0) return [];
    return serializeWithHiddenExistsHoisted(list);
}

/**
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} left
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} right
 * @returns {boolean}
 */
export function selectorClausesEqual(left, right) {
    const a = left || [];
    const b = right || [];
    if (a.length !== b.length) return false;
    return a.every((clause, index) => {
        const other = b[index];
        const normalized = normalizeClause(clause, index);
        const otherNormalized = normalizeClause(other, index);
        return (
            normalized.operatorBefore === otherNormalized.operatorBefore &&
            normalized.text === otherNormalized.text
        );
    });
}

/**
 * Keep the original selector array when the chain is unchanged. Otherwise write one serialized string.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @param {string[]|undefined|null} storedSelectors
 * @returns {string[]}
 */
export function selectorsForStore(clauses, storedSelectors) {
    const stored = (storedSelectors || []).map((item) => String(item)).filter((item) => item.trim());
    if (selectorClausesEqual(clauses, parseSelectorChain(stored))) return stored;
    return serializeSelectorChain(clauses);
}

/**
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function visibleSelectorClauses(clauses) {
    return (clauses || []).filter(
        (clause) => !isHiddenLegacyShipmentIdentifierPredicate(clause?.text)
    );
}

/**
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @param {string} text
 * @param {'and'|'or'|null|undefined} operator
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function appendSelectorClause(clauses, text, operator) {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return clauses || [];
    const next = [...(clauses || [])];
    next.push({
        text: trimmed,
        operatorBefore: next.length === 0 ? null : operator === 'or' ? 'or' : 'and'
    });
    return next;
}

/**
 * Replace visible predicate text in order. Hidden `$exists` clauses stay in the chain and are ANDed around the visible expression on save.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @param {string[]} nextVisibleTexts
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function replaceVisibleSelectorClauseTexts(clauses, nextVisibleTexts) {
    const nextVisible = (nextVisibleTexts || []).map((item) => String(item).trim()).filter(Boolean);
    const result = [];
    let visibleIndex = 0;
    for (const clause of clauses || []) {
        if (isHiddenLegacyShipmentIdentifierPredicate(clause?.text)) {
            result.push(normalizeClause(clause, result.length));
            continue;
        }
        if (visibleIndex < nextVisible.length) {
            result.push(
                normalizeClause(
                    { text: nextVisible[visibleIndex], operatorBefore: clause?.operatorBefore },
                    result.length
                )
            );
            visibleIndex += 1;
        }
    }
    while (visibleIndex < nextVisible.length) {
        result.push(
            normalizeClause(
                { text: nextVisible[visibleIndex], operatorBefore: result.length === 0 ? null : 'and' },
                result.length
            )
        );
        visibleIndex += 1;
    }
    if (result.length > 0) result[0] = { ...result[0], operatorBefore: null };
    return result;
}

/**
 * Drop one visible predicate. Each remaining clause keeps its own join.
 * The new first clause has no join. Hidden `$exists` clauses stay put.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @param {number} visibleIndex
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function removeVisibleSelectorClause(clauses, visibleIndex) {
    if (!Number.isInteger(visibleIndex) || visibleIndex < 0) return clauses || [];
    let seen = -1;
    let removed = false;
    const result = [];
    for (const clause of clauses || []) {
        if (isHiddenLegacyShipmentIdentifierPredicate(clause?.text)) {
            result.push(clause);
            continue;
        }
        seen += 1;
        if (seen === visibleIndex) {
            removed = true;
            continue;
        }
        result.push(clause);
    }
    if (!removed) return clauses || [];
    if (result.length > 0) result[0] = { ...result[0], operatorBefore: null };
    return result;
}

/**
 * Set the join on a visible clause. Index 0 is the first visible predicate and has no join.
 * @param {{ text: string, operatorBefore: 'and'|'or'|null }[]|undefined|null} clauses
 * @param {number} visibleIndex
 * @param {'and'|'or'} operator
 * @returns {{ text: string, operatorBefore: 'and'|'or'|null }[]}
 */
export function setVisibleSelectorJoin(clauses, visibleIndex, operator) {
    if (visibleIndex <= 0) return clauses || [];
    const op = operator === 'or' ? 'or' : 'and';
    let seen = -1;
    return (clauses || []).map((clause) => {
        if (isHiddenLegacyShipmentIdentifierPredicate(clause?.text)) return clause;
        seen += 1;
        if (seen === visibleIndex) return { ...clause, operatorBefore: op };
        return clause;
    });
}

/**
 * Expands stored selector lines (incl. merged `pred1 and pred2`) into individual predicates.
 * @param {string[]|undefined|null} items
 * @returns {string[]}
 */
export function expandSelectorItemsToPredicates(items) {
    return parseSelectorChain(items)
        .map((clause) => clause.text)
        .filter(Boolean);
}

/**
 * Legacy auto-generated shipment-identifier existence checks (from V1 conversion) hidden from the UI.
 * ShipperIdentifiers and CarrierIdentifiers added via QuickAdd remain visible and editable.
 * Hidden predicates stay in stored state and are still sent to the API on save.
 * @param {unknown} predicate
 * @returns {boolean}
 */
export function isHiddenLegacyShipmentIdentifierPredicate(predicate) {
    const text = String(predicate ?? '').trim();
    if (!text) {
        return false;
    }

    return /^\$exists\s*\(\s*ReferenceNumbersList\b/i.test(text);
}

/**
 * Selector predicates shown in the UI (accordion header + chip list).
 * @param {string[]|undefined|null} items Stored API selector lines for one sharing rule group.
 * @returns {string[]}
 */
export function selectorPredicatesForDisplay(items) {
    return expandSelectorItemsToPredicates(items).filter(
        (p) => !isHiddenLegacyShipmentIdentifierPredicate(p)
    );
}

/**
 * Applies chip-list edits to the full stored selector list, preserving hidden system predicates.
 * @param {string[]|undefined|null} allItems
 * @param {string[]} nextVisibleItems
 * @returns {string[]}
 */
export function mergeVisibleSelectorChanges(allItems, nextVisibleItems) {
    return replaceVisibleSelectorClauseTexts(parseSelectorChain(allItems), nextVisibleItems).map(
        (clause) => clause.text
    );
}

function unescapeJsonataSingleQuotedString(value) {
    return String(value).replace(/\\'/g, "'").replace(/\\\\/g, '\\');
}

/** Mirrors backend `OrganizationIdentifierType` names for selector header chips. */
const ORGANIZATION_IDENTIFIER_TYPE_LABELS = Object.freeze({
    0: 'Unknown',
    1: 'Scac',
    2: 'TenFourXid',
    3: 'ShipperId',
    4: 'VisibilityProviderId',
    5: 'Dot',
    6: 'Mc',
    7: 'FleetManagementCustomerId'
});

/**
 * @param {number} typeNum
 * @returns {string}
 */
function organizationIdentifierTypeLabel(typeNum) {
    if (Number.isInteger(typeNum) && ORGANIZATION_IDENTIFIER_TYPE_LABELS[typeNum] != null) {
        return ORGANIZATION_IDENTIFIER_TYPE_LABELS[typeNum];
    }
    return String(typeNum);
}

/**
 * @param {string} text
 * @returns {string[]}
 */
function headerSummariesFromOrgIdentifierPredicate(text) {
    const orgTypeMatch = text.match(
        /^(?:Shipper|Carrier)Identifiers\[OrganizationIdentifierType=(\d+)\]/i
    );
    if (!orgTypeMatch) {
        return [];
    }
    const typeNum = Number(orgTypeMatch[1]);
    if (!Number.isInteger(typeNum) || typeNum < 0) {
        return [];
    }
    return [organizationIdentifierTypeLabel(typeNum)];
}

/**
 * Short label for accordion header chips; falls back to field=value or truncated expression.
 * @param {unknown} value
 * @returns {string[]}
 */
export function headerSummariesFromSelectorPredicate(value) {
    const text = String(value ?? '').trim();
    if (!text) {
        return [];
    }

    const orgIdentifierSummaries = headerSummariesFromOrgIdentifierPredicate(text);
    if (orgIdentifierSummaries.length > 0) {
        return orgIdentifierSummaries;
    }

    const keys = [];
    const keyPredicateRegex = /\bKey\s*=\s*'((?:\\'|[^'])*)'/gi;
    let match = keyPredicateRegex.exec(text);
    while (match) {
        keys.push(unescapeJsonataSingleQuotedString(match[1]));
        match = keyPredicateRegex.exec(text);
    }

    const mapKeyRegex = /\[\s*'((?:\\'|[^'])*)'\s*\]/g;
    match = mapKeyRegex.exec(text);
    while (match) {
        keys.push(unescapeJsonataSingleQuotedString(match[1]));
        match = mapKeyRegex.exec(text);
    }

    if (keys.length > 0) {
        return keys;
    }

    const customerReferenceMatch = text.match(/\.CustomerReference\s*=\s*'/i);
    if (customerReferenceMatch) {
        return ['CustomerReference'];
    }

    const fieldRhsMatch = text.match(/\.([A-Za-z_][\w]*)\s*=\s*'((?:\\'|[^'])*)'/);
    if (fieldRhsMatch) {
        const field = fieldRhsMatch[1];
        const rhs = unescapeJsonataSingleQuotedString(fieldRhsMatch[2]);
        return [`${field}=${rhs}`];
    }

    const maxLen = 48;
    if (text.length > maxLen) {
        return [`${text.slice(0, maxLen - 3)}...`];
    }

    return [text];
}
